/**
 * TTS 오디오 캐시를 위한 IndexedDB 서비스
 */

interface TTSCacheItem {
  key: string;
  audioData: ArrayBuffer;
  timestamp: number;
}

export class TTSCacheService {
  private static readonly DB_NAME = 'BibleTTSCache';
  private static readonly DB_VERSION = 1;
  private static readonly STORE_NAME = 'audioCache';
  private static readonly MAX_AGE = 365 * 24 * 60 * 60 * 1000; // 1년

  /**
   * IndexedDB 데이터베이스 초기화
   */
  private static async openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const store = db.createObjectStore(this.STORE_NAME, { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * 캐시 키 생성
   */
  static generateCacheKey(
    version: string,
    bookCode: string,
    chapter: number,
    verse: number,
    voiceName: string,
    speakingRate: number
  ): string {
    return `${version}-${bookCode}-${chapter}-${verse}-${voiceName}-${speakingRate}`;
  }

  /**
   * 오디오 데이터를 캐시에서 조회
   */
  static async getAudioFromCache(key: string): Promise<ArrayBuffer | null> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);
      
      return new Promise((resolve, reject) => {
        const request = store.get(key);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          const result = request.result as TTSCacheItem | undefined;
          if (result) {
            // 캐시 만료 확인
            const now = Date.now();
            if (now - result.timestamp < this.MAX_AGE) {
              resolve(result.audioData);
            } else {
              // 만료된 캐시 삭제
              this.deleteFromCache(key);
              resolve(null);
            }
          } else {
            resolve(null);
          }
        };
      });
    } catch (error) {
      console.error('캐시 조회 중 오류:', error);
      return null;
    }
  }

  /**
   * 오디오 데이터를 캐시에 저장
   */
  static async saveAudioToCache(key: string, audioData: ArrayBuffer): Promise<void> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      const cacheItem: TTSCacheItem = {
        key,
        audioData,
        timestamp: Date.now()
      };
      
      await new Promise<void>((resolve, reject) => {
        const request = store.put(cacheItem);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
      
      console.log('TTS 오디오 캐시 저장 완료:', key);
    } catch (error) {
      console.error('캐시 저장 중 오류:', error);
    }
  }

  /**
   * 캐시에서 특정 항목 삭제
   */
  static async deleteFromCache(key: string): Promise<void> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      await new Promise<void>((resolve, reject) => {
        const request = store.delete(key);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    } catch (error) {
      console.error('캐시 삭제 중 오류:', error);
    }
  }

  /**
   * 만료된 캐시 정리
   */
  static async cleanExpiredCache(): Promise<void> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      const index = store.index('timestamp');
      
      const now = Date.now();
      const range = IDBKeyRange.upperBound(now - this.MAX_AGE);
      
      await new Promise<void>((resolve, reject) => {
        const request = index.openCursor(range);
        request.onerror = () => reject(request.error);
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            resolve();
          }
        };
      });
      
      console.log('만료된 TTS 캐시 정리 완료');
    } catch (error) {
      console.error('캐시 정리 중 오류:', error);
    }
  }

  /**
   * 전체 캐시 크기 조회
   */
  static async getCacheSize(): Promise<number> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);
      
      return new Promise((resolve, reject) => {
        const request = store.count();
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });
    } catch (error) {
      console.error('캐시 크기 조회 중 오류:', error);
      return 0;
    }
  }

  /**
   * 전체 캐시 삭제
   */
  static async clearAllCache(): Promise<void> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      await new Promise<void>((resolve, reject) => {
        const request = store.clear();
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
      
      console.log('전체 TTS 캐시 삭제 완료');
    } catch (error) {
      console.error('캐시 삭제 중 오류:', error);
      throw error;
    }
  }
}
